import type { StateCreator } from 'zustand';
import type {
  Booking,
  CostLineItem,
  CustomEntry,
  Journey,
  Place,
  Stay,
  Trip,
} from '@my-fullstack-app/itinerary-types';
import type { CostBearing } from '../../lib/cost';
import { generateId } from '../../lib/id';
import type { ItineraryState } from '../itinerary-store';
import { markTripDirty } from '../sync-bridge';
import type { TripField } from '../../lib/sync-manager';
import { track } from '../../lib/analytics';

/**
 * Per-line-item cost CRUD — ADR-0069. Replaces the
 * `cost-attribution-slice` of ADR-0059: cost amount, currency,
 * label, and attribution (`paidBy` / `splitWith` / `categoryId`)
 * are all per-item now and live inline on the entity.
 *
 * Bookings on Journey / Stay auto-materialise when the first
 * line item is added — matches the legacy `pricePaid` UX where
 * setting a price implicitly created the Booking.
 */

/**
 * Patch convention for a line item:
 *  - a present key with a concrete value (incl. `''` / `[]`) assigns it,
 *  - `null` clears the field (deletes the key — restoring its absent
 *    default, e.g. `splitWith: null` → "split with everyone"),
 *  - `undefined` is a no-op, so callers can spread optional values
 *    without clobbering existing fields.
 */
type ItemPatch = {
  [K in keyof Omit<CostLineItem, 'id'>]?: CostLineItem[K] | null;
};

/** Which top-level field of `Trip` was touched, so the right
 *  dirty marker fires. Day-scoped writes go through `days`
 *  (same as ADR-0048's legacy path); trip-scoped journeys /
 *  stays go through their own field. */
function dirtyFieldsFor(kind: CostBearing['kind']): TripField[] {
  switch (kind) {
    case 'place':
    case 'custom':
      return ['days'];
    case 'journey':
      return ['journeys'];
    case 'stay':
      return ['stays'];
  }
}

export interface CostItemsSlice {
  /** Append a new line item to a cost-bearing entity. Returns
   *  the minted id so callers (UI) can drive focus into the
   *  fresh row. */
  addCostItem: (
    tripId: string,
    bearer: CostBearing,
    item: Omit<CostLineItem, 'id'>,
  ) => string;
  /** Patch an existing line item. Pass `null` on any field to
   *  clear it (matches the partial-patch convention of the
   *  other slices). Item-not-found is a no-op. */
  updateCostItem: (
    tripId: string,
    bearer: CostBearing,
    itemId: string,
    patch: ItemPatch,
  ) => void;
  /** Remove a single line item. */
  removeCostItem: (tripId: string, bearer: CostBearing, itemId: string) => void;
}

function withItemsOnPlace(place: Place, items: CostLineItem[] | undefined): Place {
  if (items === undefined || items.length === 0) {
    const { costs: _drop, ...rest } = place;
    return rest;
  }
  return { ...place, costs: items };
}

function withItemsOnCustom(custom: CustomEntry, items: CostLineItem[] | undefined): CustomEntry {
  if (items === undefined || items.length === 0) {
    const { costs: _drop, ...rest } = custom;
    return rest;
  }
  return { ...custom, costs: items };
}

function withItemsOnBooking(booking: Booking | undefined, items: CostLineItem[] | undefined): Booking | undefined {
  if (items === undefined || items.length === 0) {
    if (!booking) return undefined;
    const { costs: _drop, ...rest } = booking;
    return Object.keys(rest).length === 0 ? undefined : rest;
  }
  return { ...(booking ?? {}), costs: items };
}

function mapDayWithBearer(
  trip: Trip,
  bearer: CostBearing,
  transform: (items: CostLineItem[]) => CostLineItem[] | undefined,
): Trip {
  switch (bearer.kind) {
    case 'place':
      return {
        ...trip,
        days: trip.days.map((d) => ({
          ...d,
          places: d.places.map((p) =>
            p.id === bearer.entity.id ? withItemsOnPlace(p, transform(p.costs ?? [])) : p,
          ),
        })),
      };
    case 'custom':
      return {
        ...trip,
        days: trip.days.map((d) =>
          d.customs
            ? {
                ...d,
                customs: d.customs.map((c) =>
                  c.id === bearer.entity.id ? withItemsOnCustom(c, transform(c.costs ?? [])) : c,
                ),
              }
            : d,
        ),
      };
    case 'journey':
      return {
        ...trip,
        journeys: (trip.journeys ?? []).map((j) =>
          j.id === bearer.entity.id
            ? mapJourneyBooking(j, transform)
            : j,
        ),
      };
    case 'stay':
      return {
        ...trip,
        stays: (trip.stays ?? []).map((s) =>
          s.id === bearer.entity.id
            ? mapStayBooking(s, transform)
            : s,
        ),
      };
  }
}

function mapJourneyBooking(
  journey: Journey,
  transform: (items: CostLineItem[]) => CostLineItem[] | undefined,
): Journey {
  const next = withItemsOnBooking(journey.booking, transform(journey.booking?.costs ?? []));
  return next ? { ...journey, booking: next } : (() => {
    const { booking: _drop, ...rest } = journey;
    return rest;
  })();
}

function mapStayBooking(
  stay: Stay,
  transform: (items: CostLineItem[]) => CostLineItem[] | undefined,
): Stay {
  const next = withItemsOnBooking(stay.booking, transform(stay.booking?.costs ?? []));
  return next ? { ...stay, booking: next } : (() => {
    const { booking: _drop, ...rest } = stay;
    return rest;
  })();
}

function applyItemPatch(item: CostLineItem, patch: ItemPatch): CostLineItem {
  const next: CostLineItem = { ...item };
  const target = next as unknown as Record<string, unknown>;
  for (const key of Object.keys(patch) as (keyof ItemPatch)[]) {
    const value = patch[key];
    // Patch convention: `undefined` = no-op; `null` clears the field
    // (deletes the key so it falls back to its absent default — e.g.
    // `splitWith: null` restores "split with everyone"); anything
    // else (including an empty string / empty array) is an assignment.
    if (value === undefined) continue;
    if (value === null) {
      delete target[key];
      continue;
    }
    target[key] = value;
  }
  return next;
}

export const createCostItemsSlice: StateCreator<
  ItineraryState,
  [],
  [],
  CostItemsSlice
> = (set, get) => ({
  addCostItem: (tripId, bearer, itemInput) => {
    get()._capture();
    const id = generateId();
    const item: CostLineItem = { ...itemInput, id };
    set((s) => ({
      trips: s.trips.map((t) =>
        t.id === tripId
          ? mapDayWithBearer(t, bearer, (items) => [...items, item])
          : t,
      ),
    }));
    markTripDirty(tripId, dirtyFieldsFor(bearer.kind));
    track('cost.item.added', {
      tripId,
      bearerKind: bearer.kind,
      currency: item.currency,
      hasLabel: !!item.label,
      hasPayer: !!item.paidBy,
      hasCategory: !!item.categoryId,
    });
    return id;
  },

  updateCostItem: (tripId, bearer, itemId, patch) => {
    get()._capture();
    let changedAttribution = false;
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        return mapDayWithBearer(t, bearer, (items) =>
          items.map((it) => {
            if (it.id !== itemId) return it;
            const next = applyItemPatch(it, patch);
            if (
              next.paidBy !== it.paidBy ||
              next.splitWith !== it.splitWith ||
              next.categoryId !== it.categoryId
            ) {
              changedAttribution = true;
            }
            return next;
          }),
        );
      }),
    }));
    markTripDirty(tripId, dirtyFieldsFor(bearer.kind));
    if (changedAttribution) {
      track('cost.item.attributed', { tripId, bearerKind: bearer.kind });
    }
  },

  removeCostItem: (tripId, bearer, itemId) => {
    get()._capture();
    set((s) => ({
      trips: s.trips.map((t) =>
        t.id === tripId
          ? mapDayWithBearer(t, bearer, (items) => items.filter((it) => it.id !== itemId))
          : t,
      ),
    }));
    markTripDirty(tripId, dirtyFieldsFor(bearer.kind));
  },
});
